import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../layout/header';
import Footer from '../layout/footer';
import useAnalyticsStatus from '../hooks/useAnalyticsStatus';
import useMetaStatus from '../hooks/useMetaStatus';
import './AnalyticsHubPage.css';

const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
    <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const AnalyticsHubPage = () => {
  const navigate = useNavigate();
  const { status: gaStatus, analyticsInfo } = useAnalyticsStatus();
  const { status: metaStatus, metaInfo }     = useMetaStatus();
  const connectedCount = [gaStatus, metaStatus].filter(s => s === 'connected').length;

  return (
    <div>
      <Header />
      <div className="content-wrapper">
        <div className="ah-container">

          {/* ── Compact Hero ── */}
          <div className="ah-hero">
            <div className="ah-hero-inner">
              <div className="ah-hero-left">

                {/* Title row */}
                <div className="ah-hero-title-row">
                  <div className="ah-eyebrow">
                    <span className="ah-eyebrow-dot" />
                    Analytics
                  </div>
                  <h1 className="ah-title">
                    Track your store's&nbsp;
                    <span className="ah-title-accent">performance</span>
                  </h1>
                </div>

                {/* Subtitle + status pills */}
                <div className="ah-hero-bottom">
                  <p className="ah-subtitle">
                    Connect tools to measure growth and ad ROI
                  </p>
                  <div className="ah-hero-stats">
                    <div className={`ah-stat-pill ${gaStatus === 'connected' ? 'is-connected' : ''}`}>
                      <span className={`ah-stat-dot ${gaStatus === 'connected' ? 'on' : ''}`} />
                      Google Analytics {gaStatus === 'connected' ? 'On' : 'Off'}
                    </div>
                    <div className={`ah-stat-pill ${metaStatus === 'connected' ? 'is-connected' : ''}`}>
                      <span className={`ah-stat-dot ${metaStatus === 'connected' ? 'on' : ''}`} />
                      Meta Pixel {metaStatus === 'connected' ? 'On' : 'Off'}
                    </div>
                  </div>
                </div>

              </div>

              {/* Score — right side */}
              <div className="ah-hero-right">
                <div className="ah-score-block">
                  <div className="ah-score-number">
                    {connectedCount}<span>/2</span>
                  </div>
                  <div className="ah-score-label">Active Tools</div>
                </div>
              </div>
            </div>
          </div>

          {/* Section label */}
          <div className="ah-section-label">Choose your integration</div>

          {/* ── Cards ── */}
          <div className="ah-cards">

            {/* Google Analytics */}
            <div className={`ah-card ${gaStatus === 'connected' ? 'ah-card-connected' : ''}`}>
              {gaStatus === 'connected' && <div className="ah-connected-strip" />}
              {gaStatus === 'loading' ? (
                <div className="ah-skeleton">
                  <div className="ah-skeleton-header" />
                  <div className="ah-skeleton-title" />
                  <div className="ah-skeleton-text" />
                  <div className="ah-skeleton-text" />
                  <div className="ah-skeleton-button" />
                </div>
              ) : (
                <>
                  <div className="ah-card-header">
                    <div className="ah-logo-container">
                      <svg width="26" height="26" viewBox="0 0 48 48">
                        <path fill="#EA4335" d="M24 9.5C27.7 9.5 31 10.9 33.4 13.2L40.1 6.5C36.1 2.9 30.3.5 24 .5 14.7.5 6.8 5.8 2.9 13.6l7.8 6.1C12.7 14 17.9 9.5 24 9.5Z"/>
                        <path fill="#FBBC05" d="M46.5 24c0-1.7-.2-3.3-.5-4.9H24v9.4h12.8c-.6 2.8-2.2 5.2-4.6 6.8l7.7 6c4.3-4 6.6-10.2 6.6-17.3Z"/>
                        <path fill="#34A853" d="M10.7 28.3C10.2 26.9 9.9 25.5 9.9 24c0-1.5.3-2.9.8-4.3L2.9 13.6C1.1 17.1 0 21 0 24c0 3 1.1 6.9 2.9 10.4l7.8-6.1Z"/>
                        <path fill="#4285F4" d="M24 47.5c6.3 0 11.7-2.1 15.9-6.2l-7.7-6c-2.1 1.4-4.9 2.2-8.2 2.2-6.1 0-11.3-4.5-13.3-10.2l-7.8 6.1C6.8 41.2 14.7 47.5 24 47.5Z"/>
                      </svg>
                    </div>
                    {gaStatus === 'connected' && (
                      <div className="ah-badge ah-badge-connected">
                        <CheckIcon /> Connected
                      </div>
                    )}
                  </div>
                  <h3 className="ah-card-title">Google Analytics 4</h3>
                  <p className="ah-card-description">
                    Track sessions, traffic sources, top pages and user behaviour across your entire store.
                  </p>
                  {gaStatus === 'connected' && analyticsInfo?.propertyName && (
                    <div className="ah-property-info">
                      <span className="ah-property-label">Property</span>
                      <span className="ah-property-value">{analyticsInfo.propertyName}</span>
                    </div>
                  )}
                  <div className="ah-chips">
                    <span className="ah-chip">Sessions</span>
                    <span className="ah-chip">Traffic sources</span>
                    <span className="ah-chip">Top pages</span>
                    <span className="ah-chip">Bounce rate</span>
                  </div>
                  <button
                    className={`ah-button ${gaStatus === 'connected' ? 'ah-button-secondary' : 'ah-button-primary'}`}
                    onClick={() => navigate('/analytics')}
                  >
                    {gaStatus === 'connected' ? <><span>View Dashboard</span><ArrowIcon /></> : 'Connect Google Analytics'}
                  </button>
                </>
              )}
            </div>

            {/* Meta Pixel */}
            <div className={`ah-card ${metaStatus === 'connected' ? 'ah-card-connected' : ''}`}>
              {metaStatus === 'connected' && <div className="ah-connected-strip" />}
              {metaStatus === 'loading' ? (
                <div className="ah-skeleton">
                  <div className="ah-skeleton-header" />
                  <div className="ah-skeleton-title" />
                  <div className="ah-skeleton-text" />
                  <div className="ah-skeleton-text" />
                  <div className="ah-skeleton-button" />
                </div>
              ) : (
                <>
                  <div className="ah-card-header">
                    <div className="meta-brand-icons">
                      {/* Facebook */}
                      <div className="meta-brand-icon meta-brand-icon-fb">
                        <svg viewBox="0 0 24 24" width="18" height="18">
                          <path fill="#fff" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.994 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                      </div>
                      {/* Instagram */}
                      <div className="meta-brand-icon meta-brand-icon-ig">
                        <svg viewBox="0 0 24 24" width="18" height="18">
                          <path fill="#fff" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                        </svg>
                      </div>
                    </div>
                    {metaStatus === 'connected' && (
                      <div className="ah-badge ah-badge-connected">
                        <CheckIcon /> Connected
                      </div>
                    )}
                  </div>
                  <h3 className="ah-card-title">Meta Pixel</h3>
                  <p className="ah-card-platforms">Facebook & Instagram</p>
                  <p className="ah-card-description">
                    Track visitors, conversions and pixel events from both Facebook and Instagram ads — one pixel, both platforms.
                  </p>
                  {metaStatus === 'connected' && metaInfo?.pixelId && (
                    <div className="ah-property-info">
                      <span className="ah-property-label">Pixel ID</span>
                      <span className="ah-property-value">{metaInfo.pixelId}</span>
                    </div>
                  )}
                  <div className="ah-chips">
                    <span className="ah-chip">Facebook</span>
                    <span className="ah-chip">Instagram</span>
                    <span className="ah-chip">Purchases</span>
                    <span className="ah-chip">Ad ROI</span>
                  </div>
                  <button
                    className={`ah-button ${metaStatus === 'connected' ? 'ah-button-secondary' : 'ah-button-primary'}`}
                    onClick={() => navigate('/meta')}
                  >
                    {metaStatus === 'connected' ? <><span>View Dashboard</span><ArrowIcon /></> : 'Connect Meta Pixel'}
                  </button>
                </>
              )}
            </div>

          </div>

          <p className="ah-footer-note">
            Data updates every 24 hours · Pixel events tracked in real-time
          </p>

        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AnalyticsHubPage;